import { Link } from "../../components/Link";
import { GuideCard } from "../../guide/GuideCard";
import { NAME_SELF_ENTRY, STUDENTS_ITEMS } from "../../guide/guideSteps";

import type { StudentAccount } from "@canvaschool/contracts";

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString("ko-KR") : "-";

export const StudentsPanel = ({
  roster,
  canManage,
  csvUrl,
  handoutPath,
  guideUserId,
  onShared,
  onRename,
  onClearName,
  onToggle,
  onResetPassword,
}: {
  roster: StudentAccount[];
  canManage: boolean;
  csvUrl: string;
  /** 학생마다 아이디를 채운 안내문을 인쇄하는 화면. */
  handoutPath: string;
  /** Teachers see the how-to card; omitted for anyone else. */
  guideUserId?: string;
  /** Called after the roster is printed or downloaded. */
  onShared?: () => void;
  onRename: (student: StudentAccount) => void;
  onClearName: (student: StudentAccount) => void;
  onToggle: (student: StudentAccount) => void;
  onResetPassword: (student: StudentAccount) => void;
}) => (
  <section aria-labelledby="canvaschool-roster-heading">
    {guideUserId && (
      <GuideCard
        id="students"
        title="학생 계정 이렇게 씁니다"
        userId={guideUserId}
        details={STUDENTS_ITEMS}
        ordered
      >
        「학생별 안내문 인쇄」로 아이마다 한 장씩 나눠 주세요. 반 전체 명단은
        선생님 보관용입니다. 이름은 학생이 직접 입력합니다.
      </GuideCard>
    )}
    <div className="canvaschool-title-row">
      <h2 id="canvaschool-roster-heading">학생 계정</h2>
      <div className="canvaschool-actions">
        <button
          type="button"
          onClick={() => {
            onShared?.();
            window.print();
          }}
        >
          명단 인쇄
        </button>
        {roster.some((student) => student.enabled) && (
          <Link to={handoutPath} onClick={() => onShared?.()}>
            학생별 안내문 인쇄
          </Link>
        )}
        <a href={csvUrl} download onClick={() => onShared?.()}>
          CSV 내려받기
        </a>
      </div>
    </div>
    {canManage && (
      <p className="canvaschool-hint canvaschool-no-print">
        {NAME_SELF_ENTRY} 이름을 잘못 입력한 학생만 ‘이름 수정’으로 고쳐 주세요.
      </p>
    )}
    <div className="canvaschool-table-wrap">
      <table className="canvaschool-table canvaschool-roster">
        <thead>
          <tr>
            <th scope="col">번호</th>
            <th scope="col">아이디</th>
            <th scope="col">초기 비밀번호</th>
            <th scope="col">이름</th>
            <th scope="col">상태</th>
            <th scope="col">마지막 로그인</th>
            {canManage && (
              <th scope="col" className="canvaschool-manage-column">
                관리
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {roster.map((student) => {
            const n = student.studentNumber;
            return (
              <tr key={student.userId} aria-label={`${n}번 ${student.loginId}`}>
                <td data-label="번호">{n}</td>
                <td className="canvaschool-prefix" data-label="아이디">
                  {student.loginId}
                </td>
                <td data-label="초기 비밀번호">
                  {student.initialPassword ?? "변경됨"}
                </td>
                <td data-label="이름">
                  {student.displayName ?? "(학생이 입력 전)"}
                </td>
                <td data-label="상태">
                  <span
                    className={`canvaschool-status ${
                      student.enabled
                        ? "canvaschool-status--on"
                        : "canvaschool-status--off"
                    }`}
                  >
                    {student.enabled ? "사용 중" : "사용 중지됨"}
                  </span>
                </td>
                <td data-label="마지막 로그인">
                  {formatDate(student.lastLoginAt)}
                </td>
                {canManage && (
                  <td className="canvaschool-manage-column">
                    <div className="canvaschool-actions">
                      <button
                        type="button"
                        aria-label={`${n}번 이름 수정`}
                        onClick={() => onRename(student)}
                      >
                        이름 수정
                      </button>
                      <button
                        type="button"
                        aria-label={`${n}번 이름 지우기`}
                        onClick={() => onClearName(student)}
                      >
                        이름 지우기
                      </button>
                      <button
                        type="button"
                        aria-label={`${n}번 ${
                          student.enabled ? "사용 중지" : "다시 사용"
                        }`}
                        onClick={() => onToggle(student)}
                      >
                        {student.enabled ? "사용 중지" : "다시 사용"}
                      </button>
                      <button
                        type="button"
                        aria-label={`${n}번 비밀번호 초기화`}
                        onClick={() => onResetPassword(student)}
                      >
                        비밀번호 초기화
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </section>
);
